import Component from 'vue-class-component'
import { faUndo, faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import { debounce } from 'lodash'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { BarcodeReader, BarcodeSubscriber, Form, SearchFeatured } from '@skinsoft/ui'
import { Item, ItemDataStore, Movement, MovementDataStore, SearchResult, ThesaurusTermDataStore, Wemi, isWemi, itemSelectors, movementSelectors, thesaurusTermSelectors } from '@skinsoft/entity'
import { defaultIfEmpty, filter, first, map, pluck, switchMap, tap } from 'rxjs/operators'
import { toSearchFeatured$ } from '@skinsoft/search-data-store/src/featured'
import { SimpleItemFeaturedCard } from '../simple-item-featured-card'
import { Exception } from '@zxing/library'
import { searchResultSelectors, wemiSelectors } from '@skinsoft/search-data-store'
import { ItemFeaturedCard } from '../item-featured-card'
import { Watch } from 'vue-property-decorator'
import { LinkedMovementLine } from '../linked-movement-line'
import { LINK_MOVEMENT_PAGE_NAVIGATE } from '../../../../events'

require('./select-item-multiple-form.scss')

const PAGE_SIZE = 5
@Component({
    components: {
        sBarcodeReader: BarcodeReader,
        sBarcodeSubscriber: BarcodeSubscriber,
        sItemFeaturedCard: ItemFeaturedCard,
        sSimpleItemFeaturedCard: SimpleItemFeaturedCard,
        sLinkedMovementLine: LinkedMovementLine,
    },
    props: {
        types: Array,
        excludedIds: {
            type: Array,
            default: () => [],
        },
        barcodeReaderEnabled: {
            type: Boolean,
            default: true
        },
        validationButtonText: {
            type: String,
            default: 'entity.addItem.form.submit'
        },
        validatingText: {
            type: String,
            default: 'entity.addItem.form.submitting'
        },
        disableConfirmation: {
            type: Boolean,
            default: false
        },
        showLinksToMovements: {
            type: Boolean,
            default: false
        },
        excludedItemsWarningMessageKey: {
            type: String,
            default: 'selectItemMultipleForm.errors.resultsAmongExcludedIds'
        },
    },
    template: require('./select-item-multiple-form.html'),
})
export class SelectItemMultipleForm extends ReactiveVue {
    public undoIcon = faUndo
    public loadingIcon = faCircleNotch
    public items: (SearchFeatured | Wemi | SearchResult)[] = [] as any
    public submitting = false
    public searchPending = false
    public debounce = debounce
    public item: Item = null as any
    public fullItemCardTypes = ['PhysicalMuseumItem', 'NumericMuseumItem']

    public $itemDataStore: ItemDataStore
    private $movementDataStore: MovementDataStore

    public types: string[]
    public excludedIds: string[]
    public barcodeReaderEnabled: boolean
    public validationButtonText: string
    public validatingText: string
    public disableConfirmation: boolean
    private showLinksToMovements: boolean
    private excludedItemsWarningMessageKey: string
    
    private movementsContaingItems: { [itemId: string]: Movement[] } = {}

    public errorMessage: string = ''
    public currentPage: number = 0
    public searchContent: string = ''
    public allResults: number = 0

    private readonly INPUT_FILTER_IDENTIFIER: string = 'select-item-map-form-query'
    private LINK_MOVEMENT_PAGE_NAVIGATE = LINK_MOVEMENT_PAGE_NAVIGATE

    public $refs: {
        barcodeReader: BarcodeReader
        form: Form
    }

    public async validate(values) {
        // need to be a promise to avoid conflict with $t mixin
        const errors = {} as any
        const errorMessage = this.$t('entity.addItem.form.id.notFoundError') as string

        if (!values.id) {
            this.errorMessage = this.$t('entity.addItem.form.id.emptyError') as string
        }

        if (values.id) {
            this.searchContent = values.id
            await this.performSearch(this.searchContent, 0, errorMessage)
        }
        return errors
    }

    private async performSearch(
        id: any,
        page: number,
        errorMessage: string = this.$t('entity.addItem.form.id.emptyError') as string,
    ) {
        try {
            this.searchPending = true

            try {
                await this.tryFetchingItemById(id)
            } catch (e) {
                await this.tryIdentifierSearch(id, page)
            } finally {
                if (this.items.length === 1) {
                    this.select(this.items[0] as any)
                }

                const form = this.$refs.form.form
                this.searchPending = false

                //@ts-ignore
                form.getState().values.itemId = this.items[0]
            }
        } catch (error) {
            this.searchPending = false
            this.errorMessage = errorMessage
            this.items = null as any
        }
    }

    public onBarcodeReaderInput(input: string) {
        const form = this.$refs.form.form

        form.batch(() => {
            const idState = form.getFieldState('id')
            const fieldName = 'id'
            form.change(fieldName, input)
            form.mutators.setFieldTouched(fieldName, true)
        })
    }

    public resetSearch() {
        this.$refs.form.form.change('id', '')
        this.$refs.form.form.mutators.setFieldTouched('id', true)
        this.submitting = false
        if(this.barcodeReaderEnabled) {
            this.searchContent = ''
            this.searchPending = false
            this.items = [] 
        } else {
            this.performSearch('', 0)
        }
    }

    public next() {
        this.currentPage += 1
        this.performSearch(this.searchContent, this.currentPage)
    }

    public previous() {
        this.currentPage -= 1
        this.performSearch(this.searchContent, this.currentPage)
    }

    public get isLastPage() {
        return (this.currentPage + 1) * PAGE_SIZE >= this.allResults
    }

    public select(item: Item) {
        this.item = item
    }

    private validateSelection() {
        this.$emit('item-selected', this.item)
        this.submitting = true
    }

    private get isConfirmationDisabled() {
        return !this.items || this.items.length === 0 || this.submitting || this.disableConfirmation
    }

    private getLinkedMovements(item: SearchFeatured | Wemi | SearchResult) {
        // @ts-ignore
        return this.movementsContaingItems[item.id ?? item.itemId ?? item.item.id] ?? []
    }

    private async tryFetchingItemById(id: string) {
        const mongoIdentifierChecker = /^[0-9a-fA-F]{24}$/
        const itemFromId =
            mongoIdentifierChecker.test(id) &&
            !this.excludedIds.includes(id) &&
            (await this.$itemDataStore
                .fetchOne(id)
                .pipe(
                    switchMap((item) =>
                        toSearchFeatured$(
                            { item, manifestation: null, expression: null, work: null },
                            this.$i18n.locale,
                        ),
                    ),
                    defaultIfEmpty(undefined as any),
                )
                .toPromise())


        if (itemFromId) {
            this.items = [itemFromId]
        } else {
            throw new Exception('not an existing item id')
        }
    }

    private async tryIdentifierSearch(text: string, page: number) {
        const result = await this.$itemDataStore
            .searchAll(
                '',
                null,
                PAGE_SIZE,
                this.types,
                page,
                [
                    {
                        leftCriteria: {
                            parentField: '5c50161dca5d4700015b3c94', // identifiers (museum item)
                            criteriaList: [
                                {
                                    operator: 'equals',
                                    value: {
                                        text: `${text}`,
                                        type: 'text',
                                    },
                                    entityField: '5c50161dca5d4700015b3c92', // identifier
                                    type: 'criteria',
                                    parentKeys: [],
                                    field:
                                        'identificationItemSchema.identifiers.identifier',
                                },
                            ],
                            type: 'nestedSearch',
                        },
                        rightCriteria: {
                                parentField: '5c50161cca5d4700015b3c8a', // identifiers (accessory item)
                                criteriaList: [
                                    {
                                        operator: 'equals',
                                        value: {
                                            text: `${text}`,
                                            type: 'text',
                                        },
                                        entityField: '5c50161cca5d4700015b3c88', // identifier
                                        type: 'criteria',
                                        parentKeys: [],
                                        field:
                                            'identificationItemSchema.identifiers.identifier',
                                    },
                                ],
                                type: 'nestedSearch',
                            },
                        linkOperator: 'OR',
                        type: 'requestSearch',
                    },
                ],
                undefined,
                undefined,
                null,
                {},
                [],
            )
            .pipe(
                first(),
                pluck('allData'),
                map(
                    async (items) => {
                        let excludedItemsThatMatched = [] as (SearchResult | Wemi)[] // used to inform user that the item is already in the movement/container

                        const filteredItems = items.filter(result => {
                                if((result as any).id) {
                                    if(this.excludedIds.includes((result as any).id)) {
                                        excludedItemsThatMatched.push(result)
                                    } else {
                                        return true
                                    }
                                } else if((result as any).item?.id) {
                                    if(this.excludedIds.includes((result as any).item.id)) {
                                        excludedItemsThatMatched.push(result)
                                    } else {
                                        return true
                                    }
                                }
                
                                return false
                        })

                        let result = {
                            length: filteredItems.length,
                            excludedItemsThatMatched,
                            data: [] as (SearchFeatured | Wemi | SearchResult)[]
                        }

                        result.data = await Promise.all(
                            filteredItems.map(async (item) => {
                                let itemType = isWemi(item) ? wemiSelectors.itemType(item as Wemi) : searchResultSelectors.itemType(item as SearchResult)
                                if(this.fullItemCardTypes.includes(itemType)) {
                                    return item
                                } else {
                                    return await toSearchFeatured$(
                                        item,
                                        this.$i18n.locale,
                                    ).toPromise()
                                }
                            }),
                        )

                        return result
                    }
                ),
            )
            .toPromise()

        this.items = result.data
        this.allResults = result.length

        if(result.excludedItemsThatMatched && result.excludedItemsThatMatched.length) {
            this.errorMessage = this.$t(this.excludedItemsWarningMessageKey) as string
        }
    }

    @Watch('items')
    private onItemsChanged() {
        if(this.showLinksToMovements && this.items) {
            // @ts-ignore
            const itemsIds = this.items.map(item => item.id ?? item.item.id)
            this.$movementDataStore.getMovementContainingItems(...itemsIds).subscribe(movements => {
                this.movementsContaingItems = {}
                for(const movement of movements) { // currently getMovementContainingItems() returns an array with no doubles, if that changes we should use _.uniq(movements) in the loop
                    const itemsInMovement = movement.workflowSchema.linkableItems
                        // @ts-ignore
                        .map(linkableItem => linkableItem.linkableItem.ref)
                        .filter(itemId => itemsIds.includes(itemId))

                    for(const itemId of itemsInMovement) {
                        if(this.movementsContaingItems[itemId]) {
                            this.movementsContaingItems[itemId].push(movement)
                        } else {
                            this.movementsContaingItems[itemId] = [movement]
                        }
                    }
                }
            })
        }
    }
}
